import { capitalise } from 'lib/string/stringUtils'
import moment from 'moment'
import qs from 'qs'
import { sum, without } from 'lib/array/arrayUtils'
import config from 'constants/config'
import {
  INTL_DMY_CASUAL_FORMAT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
  INTL_LONG_DATE,
  ISO_DATE_FORMAT,
  SHORT_TIME_FORMAT_24_HOUR,
  INTL_SHORT_TIME_FORMAT_AM_PM,
} from 'constants/dateFormats'
import { AnythingCategory, EXP_CATEGORY_AIRPORT_TRANSFER_ID, TRAVEL_STYLE_TO_TARGET_AUDIENCE } from 'constants/experience'
import * as ArrayUtils from 'lib/array/arrayUtils'
import type { OrderExperienceView } from 'components/Common/OrderView/OrderItemSummaries/OrderExperienceSummaries/OrderExperienceSummary'
import { isExperienceItemUpcoming, isTransferItemUpcoming } from 'lib/order/isUpcoming'
import { ITEM_STATUS_AWAITING_DATES, ITEM_STATUS_COMPLETED, ITEM_STATUS_CREATED } from 'constants/cart'
import { convertTZ, intlDateFormatter } from 'lib/datetime/dateUtils'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import type { IntlShape } from 'react-intl'

const intlDateFormatByMomentFormat: Partial<Record<string, Intl.DateTimeFormatOptions>> = {
  'DD MMM YYYY': INTL_DMY_CASUAL_FORMAT,
  'D MMM YYYY': INTL_DMY_CASUAL_SHORT_FORMAT,
  'DD MMMM YYYY': INTL_LONG_DATE,
}

function formatExperienceDuration(duration: moment.DurationInputArg1, intl: IntlShape) {
  const durationInMinutes = moment.duration(duration).asMinutes()
  if (!Number.isFinite(durationInMinutes)) return moment.duration(duration).locale(intl.locale).humanize()

  const durationInHours = Math.round(durationInMinutes / 6) / 10
  if (durationInMinutes >= 60) {
    return intl.formatMessage(
      {
        id: 'A2n_MOHYKL0o2ar8YqxP',
        defaultMessage: '{duration, plural, one {# hour} other {# hours}}',
        description: 'Experience duration label in hours shown in the offer overview',
      },
      { duration: durationInHours },
    )
  }

  return intl.formatMessage(
    {
      id: '1QWj5Nu9fP38qJn6rnlj',
      defaultMessage: '{duration, plural, one {# minute} other {# minutes}}',
      description: 'Experience duration label in minutes shown in the offer overview',
    },
    { duration: durationInMinutes },
  )
}

export function getExperienceDurationString(experience: App.ExperienceOffer, intl: IntlShape) {
  const from = experience.duration.from
  const to = experience.duration.to
  if (from && to && from !== to) {
    return intl.formatMessage(
      {
        id: '15ykoutQ4bEb_aFhj1Fb',
        defaultMessage: 'From {fromDuration} to {toDuration}',
        description: 'Experience duration label when a range of durations is available',
      },
      {
        fromDuration: formatExperienceDuration(from, intl),
        toDuration: formatExperienceDuration(to, intl),
      },
    )
  } else if (from && from === to) {
    return capitalise(formatExperienceDuration(to, intl))
  } else if (!from && to) {
    return intl.formatMessage(
      {
        id: 'GNgGpi9Z_SPxErAF7o7q',
        defaultMessage: 'Up to {toDuration}',
        description: 'Experience duration label when only maximum duration is available',
      },
      {
        toDuration: formatExperienceDuration(to, intl),
      },
    )
  } else if (from && !to) {
    return capitalise(formatExperienceDuration(from, intl))
  }

  return intl.formatMessage({
    id: 'HK3o6zPDMD4XtDpZPtSf',
    defaultMessage: 'Flexible',
    description: 'Experience duration label when duration is flexible',
  })
}

type TotalSlots = {
  totalUsedSeatsOnServer: number
  totalUsedSeatsOnCart: number
  totalSeatsRemaining: number
  isWithinSessionLimit: boolean
}

/**
 * Gets the total number of seats already used in the cart, on the server and how many are still available in the current purchase
 * This helps to calculate the limits as there are tickets that use more than one seat. For example family or couple type ticket
 */
export function getTotalSeats(
  slot: App.ExperienceAvailabilityTimeSlot,
  ticketCounts: Record<string, number>,
): TotalSlots {
  const totalUsedSeatsOnServer = ArrayUtils.sum(slot.tickets.map(ticket => ticket.customerTicketPurchasedAmount), value => value ?? 0)
  const totalUsedSeatsOnCart = ArrayUtils.sum(Object.entries(ticketCounts), ([key, value]) => {
    const ticket = slot.tickets.find(ticket => ticket.id === key)
    return value * (ticket?.participantsPerUnit ?? 1)
  })

  let totalSeatsRemaining = slot.purchaseLimit.max - totalUsedSeatsOnCart

  if (slot.purchaseLimit.maxReason === 'LIMIT-PER-CUSTOMER') totalSeatsRemaining -= totalUsedSeatsOnServer

  return {
    totalUsedSeatsOnServer,
    totalUsedSeatsOnCart,
    totalSeatsRemaining: Math.max(0, totalSeatsRemaining),
    isWithinSessionLimit: totalSeatsRemaining >= 0,
  }
}

export type ExperienceCoordinates = [latitude: number, longitude: number, ...distanceRange: Array<number>]

export interface ExperienceListKeyParams {
  latitude?: number
  longitude?: number
  currencyCode?: string
  categoryCodes?: Array<number>
  categoryCodesNotIn?: Array<number>
  placeId?: string
  placeIdToIgnore?: string
  sortBy?: Experiences.SortTypes
  priceLte?: number
  priceGte?: number
  campaigns?: Array<string>
  distanceRange?: Array<string>
  from?: string
  to?: string
  offerId?: string
  postPurchaseOnly?: boolean
  showUnlisted?: boolean
  bounds?: string
  limit?: number
  offset?: number
  onlyCuratedByRegion?: boolean
  targetAudienceIn?: Array<App.ExperienceTargetAudience>
  hasMemberPrice?: boolean
  experienceIds?: Array<string>
  experienceIdsNotIn?: Array<string>
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  leExclusive?: boolean
  bundledPackageId?: string
  isLoyaltyEligible?: boolean
  collections?: Array<string>
  tagIds?: Array<string>
  minRating?: number
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  providerNotIn?: Array<string>
  curationData?: boolean
}

export function getExperienceListKey(
  filters: ExperienceListKeyParams = {},
) {
  const tokens = [
    filters.latitude,
    filters.longitude,
    filters.currencyCode,
    without(filters.categoryCodes ?? [], AnythingCategory.id)?.join(','),
    filters.categoryCodesNotIn?.join(','),
    filters.placeId,
    filters.sortBy,
    filters.priceLte,
    filters.priceGte,
    filters.campaigns?.join(','),
    filters.distanceRange?.join(','),
    filters.from,
    filters.to,
    filters.postPurchaseOnly,
    filters.showUnlisted,
    filters.offerId,
    filters.bounds,
    filters.limit,
    filters.offset,
    filters.hasMemberPrice,
    filters.experienceIds?.join(','),
    filters.experienceIdsNotIn?.join(','),
    filters.luxPlusFeatures?.join(','),
    filters.leExclusive,
    filters.bundledPackageId,
    filters.isLoyaltyEligible,
    filters.collections?.join(','),
    filters.tagIds?.join(','),
    filters.minRating,
    filters.targetAudience?.join(','),
    filters.hasFreeCancellation,
    filters.hasInstantConfirmation,
    filters.isGift,
    filters.coordinatesList?.map(coords => coords.join(',')).join(','),
    filters.timeSlots?.join(','),
    filters?.providerNotIn?.join(','),
    filters.curationData,
  ]

  return tokens.join('-')
}

interface DatesKeyParams {
  pickupPointId?: string
  redemptionLocationId?: string
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function getExperienceDatesKey(experienceId: string, params?: DatesKeyParams) {
  const ticketModesKey = params?.ticketModesMap && Object.entries(params.ticketModesMap).map(([key, value]) => `${key}:${value}`).join(',')
  const ticketsKey = params?.tickets?.join(',')
  return `${experienceId}-${params?.pickupPointId ?? params?.redemptionLocationId}-${params?.orderId}-${ticketsKey}-${ticketModesKey}`
}

interface TimesKeyParams {
  currency: string
  pickupPointId?: string
  redemptionLocationId?: string
  isBuyNowBookLater?: boolean
  isGift?: boolean
  tickets?: Array<string>
  ticketModesMap?: Record<string, string>
  orderId?: string
}

export function getExperienceTimesKey(experienceId: string, date?: string, params?: TimesKeyParams) {
  const ticketModesKey = params?.ticketModesMap && Object.entries(params.ticketModesMap).map(([key, value]) => `${key}:${value}`).join(',')
  return `${experienceId}-${date}-${params?.currency}-${params?.pickupPointId ?? params?.redemptionLocationId}-${params?.isBuyNowBookLater ?? false}-${params?.isGift ?? false}-${params?.orderId}-${ticketModesKey}`
}

/** Cache key for attraction offers — only fields sent to GET /attractions/:id/offers */
export function getAttractionOffersKey(attractionId: string, searchFilters?: Experiences.SearchFilters): string {
  const tokens = [
    searchFilters?.categoryCodes?.join(','),
    searchFilters?.luxPlusFeatures?.length ? 'true' : undefined,
    searchFilters?.priceLte,
    searchFilters?.priceGte,
    searchFilters?.checkIn,
    searchFilters?.checkOut,
  ]
  return `attraction-offers-${attractionId}:${tokens.join('-')}`
}

export function getExperienceTicketPurchaseKey(
  ticket: App.ExperienceItemTicket,
  count: number,
) {
  return `${ticket.id}-${count}`
}

/**
 * Returns cart data for an experience item to be used in the experience booking view
 */
export function getExperienceBookingViewCartData(
  cartExperienceItem: App.Checkout.ExperienceItem,
): App.ExperienceBookingView['cartData'] {
  const cartData = cartExperienceItem?.tickets.reduce((acc, t) => {
    const ticketPrice = t.price ?? 0
    const ticketCount = t.count ?? 0
    const ticketDiscount = t.discount ?? 0
    const ticketMemberPrice = t.memberPrice ?? 0
    return {
      totalPrice: acc.totalPrice + (ticketPrice * ticketCount),
      totalGrossPrice: acc.totalGrossPrice + ((ticketPrice + ticketDiscount) * ticketCount),
      totalMemberPrice: acc.totalMemberPrice + (ticketMemberPrice * ticketCount),
      count: acc.count + ticketCount,
      itemId: acc.itemId,
    }
  }, { totalPrice: 0, count: 0, totalGrossPrice: 0, totalMemberPrice: 0, itemId: cartExperienceItem.itemId })

  if (cartData.count > 0) return cartData
}

export function getExperienceBookingViewCartDataFromTransferItems(
  transferItems: Array<App.Checkout.TransferItem>,
): App.ExperienceBookingView['cartData'] {
  return {
    totalPrice: sum(transferItems.map(item => item.transfer.option!.price ?? 0)),
    totalGrossPrice: sum(transferItems.map(item => item.transfer.option!.price ?? 0)),
    totalMemberPrice: sum(transferItems.map(item => item.transfer.option!.memberPrice ?? 0)),
    count: transferItems.length,
  }
}

export function getExperienceCompactLocation(location: App.ExperienceOffer['location'], maxLength: number = 40) {
  if (!Array.isArray(location.marketingAddress)) return undefined

  const neighborhood = location.marketingAddress.find((place) => place.lePlaceType === 'neighborhood')?.lePlaceName
  const cityName =
    location.marketingAddress.find((place) => place.lePlaceType === 'city')?.lePlaceName
    ?? location.marketingAddress.find((place) => place.lePlaceType === 'multi_city_vicinity')?.lePlaceName
  const countryName = location.marketingAddress.find((place) => place.lePlaceType === 'country')?.lePlaceName

  if (neighborhood && cityName) {
    if (neighborhood.toLowerCase().includes(cityName.toLowerCase())) return neighborhood
    const compact = `${neighborhood}, ${cityName}`
    if (compact.length <= maxLength) return compact
    return neighborhood
  }

  if (cityName && countryName) {
    return `${cityName}, ${countryName}`
  }

  return cityName ?? countryName
}

export function mapExperienceToBookingView(
  exp: App.ExperienceOffer,
  cartExperienceItem?: App.Checkout.ExperienceItem,
  property?: App.OfferProperty | App.BedbankProperty,
): App.ExperienceBookingView {
  const intl = getCurrentIntl()
  const leExclusive = exp.leExclusive && config.BRAND === 'luxuryescapes'

  const cartData = cartExperienceItem && getExperienceBookingViewCartData(cartExperienceItem)

  return {
    id: exp.id,
    primaryCategory: exp.primaryCategory?.name,
    categories: exp.categories,
    location: exp.location.name,
    compactLocation: getExperienceCompactLocation(exp.location, 30),
    city: exp.location.city,
    image: exp.images[0],
    pricing: exp.pricing,
    price: exp.price,
    grossPrice: exp.grossPrice,
    title: exp.name,
    originalExperience: exp,
    description: exp.copy.dealDescription,
    ticketOptions: [],
    added: !!cartData,
    cartData,
    leExclusive,
    type: exp.productType,
    discount: exp.discount,
    freeCancellation: exp.cancellationPolicies.length > 0,
    interestingLocations: [
      ...(property ? [{
        id: property.id,
        latitude: property.latitude,
        longitude: property.longitude,
        type: 'property' as const,
        name: intl.formatMessage(
          {
            id: 'Vjc5CLRAFxuFVYOg1fGS',
            defaultMessage: 'Property: {propertyName}',
            description: 'Map marker label prefixing a property name in the experience booking view',
          },
          { propertyName: property.name },
        ),
      }] : []),
    ],
    ticketUnitLabel: exp.ticketUnitLabel,
    ...(exp.features?.transfer && {
      features: {
        transfer: {
          preferredTransfer: exp.features?.transfer?.preferredTransfer,
        },
      },
    }),
    rating: exp.rating,
    distance: (property && exp.redemptionLocations?.length) ?
        Math.min(...exp.redemptionLocations.map(location => calculateDistance(property.latitude, property.longitude, location.latitude, location.longitude))) :
      undefined,
    vendor: exp.vendor,
    disableReviews: exp.disableReviews,
    tagIds: exp.tagIds ?? [],
  }
}

export interface QueryStringSearchParams {
  destinationId?: string
  destinationName?: string
  experienceId?: string
  experienceName?: string
  [key: string]: any
}

export function replaceExperienceQueryString(params: QueryStringSearchParams) {
  const { destinationId, destinationName, ...rest } = params

  const query = qs.stringify({
    destinationId,
    destinationName,
    ...rest,
  }, { arrayFormat: 'comma' })

  return decodeURI(query).replace(/\s/g, '+')
}

/**
 * Filters experience items to only include standalone experiences
 * (those without an accommodation_parent_item_id linking them to a hotel room)
 */
export function getNonComplimentaryExperienceItems(experienceItems: Array<App.OrderExperienceItem>): Array<App.OrderExperienceItem> {
  return experienceItems.filter(item => !item.accommodation_parent_item_id)
}

/**
 * Filters experience items to only include linked experiences
 * (those with an accommodation_parent_item_id linking them to a hotel room)
 */
export function getComplimentaryExperienceItems(experienceItems: Array<App.OrderExperienceItem>): Array<App.OrderExperienceItem> {
  return experienceItems.filter(item => !!item.accommodation_parent_item_id)
}

export function mapExperienceSummaryViews(order: App.Order, includeLinkedExperiences: boolean = true): Array<OrderExperienceView> {
  // Filter experiences based on whether they are linked to an accommodation
  const experienceItemsToProcess = includeLinkedExperiences
    ? order.experienceItems
    : getNonComplimentaryExperienceItems(order.experienceItems)

  // First, separate items by whether they have a providerOrderId
  const itemsWithProviderOrderId = experienceItemsToProcess.filter(item => item.providerOrderId != null)
  const itemsWithoutProviderOrderId = experienceItemsToProcess.filter(item => item.providerOrderId == null)

  const result: Array<OrderExperienceView> = []

  // Group items with providerOrderId by providerOrderId first, then by experienceId
  const itemsByProviderOrderId = ArrayUtils.groupBy(itemsWithProviderOrderId, item => item.providerOrderId)
  for (const providerOrderItems of itemsByProviderOrderId.values()) {
    const itemsByExperienceId = ArrayUtils.groupBy(providerOrderItems, item => item.experienceId)
    for (const items of itemsByExperienceId.values()) {
      const experienceItem = items[0]

      const itemsWithMobileAppDiscount = items.filter(item => item.mobileAppDiscountAmount > 0)
      const mobileAppDiscountAmount = itemsWithMobileAppDiscount.length ? ArrayUtils.sum(itemsWithMobileAppDiscount, item => item.mobileAppDiscountAmount ?? 0) : 0
      const mobileAppDiscountPercentage = itemsWithMobileAppDiscount.length ? ArrayUtils.sum(itemsWithMobileAppDiscount, item => item.mobileAppDiscountPercentage ?? 0) / itemsWithMobileAppDiscount.length : 0

      result.push({
        experienceId: experienceItem.experienceId,
        tickets: items.map(item => ({
          item,
          name: item.ticket.name,
          purchaseId: item.bookingNumber,
          price: item.total + item.mobileAppDiscountAmount,
          cancelled: item.status === 'cancelled',
          refundItem: order.refunds.find(refund => refund.itemId === item.id),
        })),
        mobileAppDiscountAmount,
        mobileAppDiscountPercentage,
        total: ArrayUtils.sum(items, item => item.total),
        item: experienceItem,
        cancelled: items.every(item => item.status === 'cancelled'),
      })
    }
  }

  // Group items without providerOrderId by experienceId, date, time and redemptionLocation
  const itemsByExperienceId = ArrayUtils.groupBy(itemsWithoutProviderOrderId, item => [item.experienceId, item.date, item.time, item.redemptionLocation?.id].join('-'))
  for (const items of itemsByExperienceId.values()) {
    const experienceItem = items[0]

    const itemsWithMobileAppDiscount = items.filter(item => item.mobileAppDiscountAmount > 0)
    const mobileAppDiscountAmount = itemsWithMobileAppDiscount.length ? ArrayUtils.sum(itemsWithMobileAppDiscount, item => item.mobileAppDiscountAmount ?? 0) : 0
    const mobileAppDiscountPercentage = itemsWithMobileAppDiscount.length ? ArrayUtils.sum(itemsWithMobileAppDiscount, item => item.mobileAppDiscountPercentage ?? 0) / itemsWithMobileAppDiscount.length : 0

    result.push({
      experienceId: experienceItem.experienceId,
      tickets: items.map(item => ({
        item,
        name: item.ticket.name,
        purchaseId: item.bookingNumber,
        price: item.total + item.mobileAppDiscountAmount,
        cancelled: item.status === 'cancelled',
        refundItem: order.refunds.find(refund => refund.itemId === item.id),
      })),
      mobileAppDiscountAmount,
      mobileAppDiscountPercentage,
      total: ArrayUtils.sum(items, item => item.total),
      item: experienceItem,
      cancelled: items.every(item => item.status === 'cancelled'),
    })
  }

  return result
}

// For bundled experiences, cancellability is governed by the parent hotel room's policy,
// not the experience's own cancellation policies or the experience refund API.
function isBundledWithRefundableHotel(order: App.Order, experienceSummary: OrderExperienceView): boolean {
  const { isAccomBundle } = experienceSummary.item
  if (!isAccomBundle) return false
  const parentBedbankItem = order.bedbankItems.find(b => b.isAccomBundle === isAccomBundle)
  if (!parentBedbankItem) return false
  return parentBedbankItem.rooms.some(room => room.refundable || room.partiallyRefundable)
}

export function canCancelExperience(order: App.Order, experienceSummary: OrderExperienceView, isExpired: boolean, bookingDetails?: Record<string, App.ExperienceBookingDetails>, refundableItems: Array<string> = [], experienceStatus?: string, provider?: string) {
  // No values left to be refundable
  const hasSomeValueToRefund = experienceSummary.tickets.some((ticket) => refundableItems.includes(ticket.item.id))
  if (!hasSomeValueToRefund) return false

  // Already cancelled
  if (experienceSummary.cancelled) return false

  // Already redeemed
  const alreadyRedeemed = experienceSummary.tickets.some((ticket) => bookingDetails?.[ticket.item.id]?.markedAsRedeemed)
  if (alreadyRedeemed) return false

  // Order locked
  if (order.locked) return false

  // Klook items take some time to book/confirm and when confirmed a webhook will populate the ProviderBookingNumber field
  // this is a temporary solution while we monitor klook requests to implement a better solution
  const klookTicketWithNoBookingNumber = (provider === 'klo' && experienceSummary.tickets
    .map((ticket) => bookingDetails?.[ticket.item.id])
    .filter(Boolean)
    .some((detail) => !detail?.providerBookingNumber))

  // Klook validations
  if (klookTicketWithNoBookingNumber) return false

  // Force refundable true for bundled
  const bundledAndRefundable = isBundledWithRefundableHotel(order, experienceSummary)
  if (bundledAndRefundable) return true
  if (experienceSummary.item.isAccomBundle) return false

  // Past ticket
  if (!isExperienceItemUpcoming(experienceSummary.item, order)) return false

  // Expired BNBL
  if (
    experienceSummary.item.status === ITEM_STATUS_AWAITING_DATES &&
    (isExpired || experienceStatus != 'ONLINE')
  ) return false

  // Return valid status
  return (
    experienceSummary.item.status === ITEM_STATUS_COMPLETED ||
    experienceSummary.item.status === ITEM_STATUS_AWAITING_DATES ||
    experienceSummary.item.status === ITEM_STATUS_CREATED
  )
}

/**
 *  The dates come as UTC and ISO format from the backend. In some cases we need to show the date/time at the event location.
 */
export function getFormattedDateByTimezone(
  date: string | undefined,
  timezone: string,
  format: string | Intl.DateTimeFormatOptions,
) {
  if (!date) return

  const zonedDate = convertTZ(new Date(date), timezone)

  if (typeof format === 'object') {
    return intlDateFormatter(zonedDate, format, getCurrentIntl().locale)
  }

  const intlDateFormat = intlDateFormatByMomentFormat[format]

  if (intlDateFormat) {
    return intlDateFormatter(zonedDate, intlDateFormat, getCurrentIntl().locale)
  }

  if (format === ISO_DATE_FORMAT) {
    return moment(zonedDate).format(ISO_DATE_FORMAT)
  }
}

export function getExistingOrderExperienceCalendarDates(
  order?: App.Order,
): Map<string, Array<App.OrderExperienceItem>> {
  if (!order) return new Map()
  const experiences = order.experienceItems?.filter((e) => isExperienceItemUpcoming(e, order)) ?? []
  const transfers = order.transferItems?.filter(isTransferItemUpcoming) ?? []
  return ArrayUtils.groupBy([...experiences, ...transfers], (e) =>
    moment(e.date).format(ISO_DATE_FORMAT),
  )
}

export interface FetchDateParams {
  pickupPointId?: string
  redemptionLocationId?: string
  ticketModesMap?: Record<string, string>
  isGift?: boolean
  isBuyNowBookLater?: boolean
}

/**
 * Returns a boolean indicating that it has already taken the data needed to fetch the available dates for an experience offer.
 */
export function isFetchDatesParamsValid(
  experience?: App.ExperienceOffer,
  fetchDateParams?: FetchDateParams,
) {
  if (!experience) return false

  const isPickupValid = !experience.pickupPoints.length || experience.pickupOptional || !!fetchDateParams?.pickupPointId
  const isLocationValid = !experience.redemptionLocations.length || !!fetchDateParams?.redemptionLocationId
  const isTicketModeValid = !experience.ticketModes?.length || !!isTickedModesValid(experience.ticketModes, fetchDateParams?.ticketModesMap ?? {})

  // For gift and BNBL we don't need locations or pickup points
  if (fetchDateParams?.isGift || fetchDateParams?.isBuyNowBookLater) return isTicketModeValid

  // For regular experiences we need to check all the fields
  return isPickupValid && isLocationValid && isTicketModeValid
}

export interface FetchTimeParams {
  pickupPointId?: string
  redemptionLocationId?: string
  ticketModesMap?: Record<string, string>
  date?: string
  isGift?: boolean
  isBuyNowBookLater?: boolean
}
/**
 * Returns a boolean indicating that it has already taken the data needed to fetch the available tickets/times for an experience offer.
 */
export function isFetchTimesParamsValid(
  experience?: App.ExperienceOffer,
  fetchTimeParams?: FetchTimeParams,
) {
  if (!experience) return false

  const isPickupValid = !experience.pickupPoints.length || experience.pickupOptional || !!fetchTimeParams?.pickupPointId
  const isLocationValid = !experience.redemptionLocations.length || !!fetchTimeParams?.redemptionLocationId
  const isTicketModeValid = !experience.ticketModes?.length || !!isTickedModesValid(experience.ticketModes, fetchTimeParams?.ticketModesMap ?? {})

  // For gift and BNBL we don't need locations or pickup points or a date, it is defaulted
  if (fetchTimeParams?.isGift || fetchTimeParams?.isBuyNowBookLater) return isTicketModeValid

  // For regular experiences we need to check all the fields
  return isPickupValid && isLocationValid && isTicketModeValid && fetchTimeParams?.date
}

/** Check if all the required field are filled */
function isTickedModesValid(fields: Array<App.TicketModeField>, data: Record<string, string>) {
  if (!fields) return true
  for (const field of fields) {
    if (field.required && !data[field.key]) return false
    const option = field.options.find(o => o.key === data[field.key])
    if (option?.fields) {
      const valid = isTickedModesValid(option.fields, data)
      if (!valid) return false
    }
  }
  return true
}

export function getExperiencesTimesKeyFromCartItem(item: App.Checkout.ExperienceItem, currency: string) {
  return getExperienceTimesKey(
    item.experienceId,
    (item.isBuyNowBookLater || item.isGift) ? undefined : item.date,
    {
      pickupPointId: item.pickupPointId,
      redemptionLocationId: item.redemptionLocationId,
      isBuyNowBookLater: item.isBuyNowBookLater,
      isGift: item.isGift,
      currency,
      ticketModesMap: item.ticketModesMap,
    },
  )
}

export function getExperienceTicketDate(date?: string, time?: string) {
  if (date && time) {
    const [hour, minute] = time.split(':')
    return moment.utc(date).minute(parseInt(minute)).hour(parseInt(hour)).format('YYYY-MM-DD[T]HH:mm:ss')
  } else {
    return moment.utc(date).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss')
  }
}

export function getTargetAudience(adults: number, children: number = 0): App.ExperienceTargetAudience {
  if (children === 0) {
    if (adults === 1) return 'SOLO'
    if (adults === 2) return 'COUPLE'
    return 'GROUP'
  } else {
    return 'FAMILY'
  }
}

export function getComplimentaryTransferParams(packageId: string) {
  return { showUnlisted: true, bundledPackageId: packageId, categoryCodes: [EXP_CATEGORY_AIRPORT_TRANSFER_ID] }
}

/**
 * Prioritises Collinson lounges over other providers, except at airports
 * where Aspire offers are available — in that case Aspire (and any other
 * non-Collinson provider) is kept and Collinson is filtered out.
 */
export function filterCollinsonLounges<T>(lounges: Array<T>, getProvider: (lounge: T) => string): Array<T> {
  const hasAspire = lounges.some(l => getProvider(l) === 'asp')
  if (hasAspire) {
    return lounges.filter(l => getProvider(l) !== 'cln')
  }
  const hasCollinson = lounges.some(l => getProvider(l) === 'cln')
  return hasCollinson ? lounges.filter(l => getProvider(l) === 'cln') : lounges
}

/**
 * Returns true if a Collinson lounge supports the required ticket mode
 * (DOMESTIC or INTERNATIONAL) based on the flight type.
 * Non-Collinson lounges and lounges without ticket mode fields always pass.
 */
export function collinsonLoungeSupportsFlightType(
  experience: App.ExperienceOffer,
  isInternational: boolean | undefined,
): boolean {
  if (experience.provider !== 'cln') return true
  if (isInternational === undefined) return true
  const ticketModes = experience.ticketModes ?? []
  const domesticIntlField = ticketModes.find(field =>
    field.options.some(opt => opt.key === 'DOMESTIC') &&
    field.options.some(opt => opt.key === 'INTERNATIONAL'),
  )
  if (!domesticIntlField) return true
  const requiredMode = isInternational ? 'INTERNATIONAL' : 'DOMESTIC'
  return domesticIntlField.options.some(opt => opt.key === requiredMode)
}

export function buildOfferTagsCacheKey(offerId: string, startDate?: string, endDate?: string): string {
  const normalizedStart = startDate ? moment.utc(startDate).startOf('day').toISOString() : undefined
  const normalizedEnd = endDate ? moment.utc(endDate).startOf('day').toISOString() : undefined
  const datePart = [normalizedStart, normalizedEnd].filter(Boolean).join(':')
  return datePart ? `${offerId}:${datePart}` : offerId
}

export function mapTravelStylesToTargetAudience(travelStyles: Array<Experiences.TravelStyleFilter>): Array<App.ExperienceTargetAudience> {
  return travelStyles.map(style => TRAVEL_STYLE_TO_TARGET_AUDIENCE[style])
}

export function getPickupTime(pickupPoint?: App.ExperiencePickupPoint, time?: string): string | undefined {
  if (!pickupPoint) return

  let parsedDate: moment.Moment | undefined
  if (pickupPoint.time) {
    parsedDate = moment(pickupPoint.time, [SHORT_TIME_FORMAT_24_HOUR])
  }
  if (pickupPoint.minutesPrior && time) {
    parsedDate = moment(time, [SHORT_TIME_FORMAT_24_HOUR]).subtract(pickupPoint.minutesPrior, 'minutes')
  }

  if (parsedDate?.isValid()) {
    return intlDateFormatter(parsedDate, INTL_SHORT_TIME_FORMAT_AM_PM, getCurrentIntl().locale)
  }
}
